import { Icon } from '@ab-core/icon';
import { getClassNames } from '@ab-core/functions';
import { getTestIdProp } from '@ab-core/testing';
import type { FC, KeyboardEvent, ReactNode } from 'react';
import React, { useRef } from 'react';
import type { TabType } from '../../types';
import { MinimizeButton, StripContainer, Tab } from './style';

export type TabStripItem = {
    key: TabType;
    head: ReactNode;
};

type TabStripProps = {
    tabs: TabStripItem[];
    activeIndex: number;
    isMinimized: boolean;
    onToggleMinimize: () => void;
    onSelectTab: (key: TabType) => void;
    tabId: (key: TabType) => string;
    panelId: (key: TabType) => string;
};

export const TabStrip: FC<TabStripProps> = (props) => {
    const { tabs, activeIndex, isMinimized, onToggleMinimize, onSelectTab, tabId, panelId } = props;
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        let nextIndex: number | null = null;

        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                nextIndex = (activeIndex + 1) % tabs.length;
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                nextIndex = (activeIndex - 1 + tabs.length) % tabs.length;
                break;
            case 'Home':
                nextIndex = 0;
                break;
            case 'End':
                nextIndex = tabs.length - 1;
                break;
            default:
                return;
        }

        event.preventDefault();
        const nextTab = tabs[nextIndex];

        if (nextTab) {
            onSelectTab(nextTab.key);
            tabRefs.current[nextIndex]?.focus();
        }
    };

    return (
        <StripContainer role="tablist" aria-label="Vergleich und Zubehör">
            <MinimizeButton
                type="button"
                aria-expanded={!isMinimized}
                aria-label={isMinimized ? 'Öffnen' : 'Minimieren'}
                onClick={onToggleMinimize}
                {...getTestIdProp('crosselling-bar-toggle')}
            >
                <Icon name={isMinimized ? 'ChevronUp' : 'ChevronDown'} size="SMALL_4" />
            </MinimizeButton>
            {tabs.map((tab, index) => {
                const isActive = !isMinimized && index === activeIndex;

                return (
                    <Tab
                        key={tab.key}
                        ref={(element) => {
                            tabRefs.current[index] = element;
                        }}
                        type="button"
                        id={tabId(tab.key)}
                        role="tab"
                        aria-selected={isActive}
                        aria-controls={panelId(tab.key)}
                        tabIndex={index === activeIndex ? 0 : -1}
                        className={getClassNames({
                            prefix: 'crosselling-tab',
                            modifier: { 'is-active': isActive }
                        })}
                        onClick={() => onSelectTab(tab.key)}
                        onKeyDown={onTabKeyDown}
                    >
                        {tab.head}
                    </Tab>
                );
            })}
        </StripContainer>
    );
};
